// Client-side del cross-sell del carrito lateral.
//
//   1) Vista rápida (<dialog>): abre por [data-quick-view-open][data-target],
//      cierra por [data-quick-view-close], click fuera del panel, o autocierre
//      cuando Horizon dispara CartLinesUpdate (item añadido).
//
//   2) Estado y orden del cross-sell (3 estados), recomputados tras cada
//      morph del drawer:
//        cover   → al menos una pieza cubre el hueco. Copy de "cover",
//                  orden: cierran primero (más barata cierra primero), luego
//                  no-cierra (chunk mayor primero).
//        partial → hay hueco pero ninguna pieza lo cubre. Copy de "partial",
//                  orden: mayor a menor precio (acerca al umbral cuanto antes).
//        free    → gap = 0. Copy de "free", orden precio asc.
//      Los tres copies (título + subtítulo) vienen del Liquid como data-*
//      del <aside>, con {{ amount }} sin resolver para poder inyectar el
//      hueco actualizado en cliente sin recargar la sección.
//
//   3) Flechas prev/next del carrusel scroll-snap. aria-disabled depende de
//      la posición de scroll real (scrollLeft, clientWidth, scrollWidth) con
//      tolerancia 2px — ver updateArrows. El paso es una fracción del
//      viewport (0.85), con target clampeado a [0, scrollWidth - clientWidth]
//      para que el último click llegue al final exacto, no se quede corto.
//
// El add-to-cart real lo hace <product-form-component> de Horizon. Aquí solo
// se orquestan DOM, dialog, estado, orden, scroll y aria-state.

import { StandardEvents } from '@shopify/events';

const OPEN_SELECTOR = '[data-quick-view-open][data-target]';
const CLOSE_SELECTOR = '[data-quick-view-close]';
const DIALOG_SELECTOR = 'dialog[data-the90s-quick-view]';
const ROOT_SELECTOR = '[data-cross-sell-orderer]';

function moneyFormat(cents) {
  const eur = (cents / 100).toFixed(2).replace(/\.00$/, '').replace('.', ',');
  return `${eur}€`;
}

function applyAmountPlaceholder(template, cents) {
  if (!template) return '';
  return template.replace(/\{\{\s*amount\s*\}\}/g, moneyFormat(cents));
}

// ---- Quick-view: apertura / cierre ------------------------------------

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const opener = event.target.closest(OPEN_SELECTOR);
  if (opener) {
    const selector = opener.getAttribute('data-target');
    if (!selector) return;
    const dialog = document.querySelector(selector);
    if (dialog instanceof HTMLDialogElement && !dialog.open) {
      event.preventDefault();
      dialog.showModal();
    }
    return;
  }

  const closer = event.target.closest(CLOSE_SELECTOR);
  if (closer) {
    const dialog = closer.closest(DIALOG_SELECTOR);
    if (dialog instanceof HTMLDialogElement) dialog.close();
    return;
  }

  // Click en backdrop: llega con target === dialog cuando cae fuera del
  // panel visible. Comparamos coordenadas contra bounding box del panel.
  if (event.target.matches(DIALOG_SELECTOR)) {
    const dialog = event.target;
    const panel = dialog.querySelector('.the90s-quick-view__panel');
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const insidePanel =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!insidePanel) dialog.close();
  }
});

// ---- Cross-sell: recomputa estado, copy, orden y paginación ------------

// Tolerancia sub-píxel al comparar scrollLeft/clientWidth/scrollWidth:
// basta con 2px para absorber redondeo del navegador. Más alto (p. ej. el
// gap de 10px) causaba el bug 2026-10-14 — con 3 cards de 46% + peek de la
// 4ª, scrollLeft + clientWidth se quedaba a ~12px de scrollWidth y la
// flecha derecha salía deshabilitada antes de llegar al final real.
const SCROLL_TOLERANCE_PX = 2;

// Paso de la paginación = fracción del viewport (no "ancho de card + gap").
// Con 85% el último click siempre queda clampeado por el navegador a
// scrollWidth - clientWidth: no hay short-landing y el último item queda
// completo, no asomando. Mismo criterio que the90s-variant-picker-track.js.
const PAGE_FRACTION = 0.85;

function computeState(root) {
  const threshold = parseInt(root.dataset.thresholdCents, 10);
  const subtotal = parseInt(root.dataset.subtotalCents, 10);
  const gap = Number.isFinite(threshold) && Number.isFinite(subtotal)
    ? Math.max(0, threshold - subtotal)
    : 0;

  const list = root.querySelector('[data-cross-sell-list]');
  const items = list ? Array.from(list.children) : [];

  // Dos estados (2026-09-22): 'free' cuando el envío gratis está conseguido,
  // 'gap' cuando falta. Se retiró el estado intermedio 'partial' junto con el
  // copy "Con una de estas te sale gratis el envío" que prometía en falso.
  if (!items.length) return { state: 'free', gap: 0, items: [] };
  if (gap === 0) return { state: 'free', gap: 0, items };
  return { state: 'gap', gap, items };
}

function sortItems(items, state, gap) {
  const withPrice = items.map((el) => ({
    el,
    price: parseInt(el.dataset.priceCents, 10) || 0,
  }));
  if (state === 'free') {
    withPrice.sort((a, b) => a.price - b.price);
  } else {
    // state === 'gap': piezas que cubren el hueco primero (aunque el copy no
    // lo diga, si existe una que lo cierre debe verse la primera). Dentro de
    // cada bloque: las que cubren, de más barata a más cara (la mínima que
    // cierra sale antes); las que no cubren, de mayor a menor precio para
    // acercarse al umbral cuanto antes.
    withPrice.sort((a, b) => {
      const aCloses = a.price >= gap;
      const bCloses = b.price >= gap;
      if (aCloses !== bCloses) return aCloses ? -1 : 1;
      if (aCloses) return a.price - b.price;
      return b.price - a.price;
    });
  }
  return withPrice.map((entry) => entry.el);
}

function applyCopyForState(root, state, gap) {
  const titleEl = root.querySelector('[data-cross-sell-title]');
  const subtitleEl = root.querySelector('[data-cross-sell-subtitle]');
  if (!titleEl) return;

  const titleTpl = root.dataset[`title${state.charAt(0).toUpperCase()}${state.slice(1)}`];
  const subtitleTpl = root.dataset[`subtitle${state.charAt(0).toUpperCase()}${state.slice(1)}`];

  // Aplicamos SIEMPRE el título correspondiente al estado. Con dos estados
  // ('gap' | 'free') y defaults hardcoded en Liquid, titleTpl no debería
  // llegar blank; el guard `|| titleEl.textContent` es defensa por si el
  // DOM se manipula externamente.
  titleEl.textContent = titleTpl || titleEl.textContent;
  if (subtitleEl && subtitleTpl) {
    subtitleEl.textContent = applyAmountPlaceholder(subtitleTpl, gap);
  }
}

function updateArrows(root) {
  // Rediseño 2026-09-21: carrusel scroll-snap horizontal con peek de la
  // siguiente card. Ya no ocultamos items con `hidden`.
  //
  // Estado de las flechas (2026-10-14 v2): calculado desde la posición de
  // scroll real, no desde una paginación por páginas. Antes se usaba
  // `state.page >= total - 1` con `total = ceil(items.length / perPage)`
  // y `perPage ∈ {1, 2}`, que se desincronizaba del scroll real cuando el
  // paso de scroll quedaba corto (short-landing por scroll-snap mandatory):
  // la flecha salía deshabilitada aunque aún asomase media card.
  //
  // Nuevo criterio:
  //   atStart ↔ scrollLeft ≤ 2
  //   atEnd   ↔ scrollLeft + clientWidth ≥ scrollWidth - 2
  //   hasOverflow ↔ scrollWidth - clientWidth > 2 (si no, ambas off).
  const list = root.querySelector('[data-cross-sell-list]');
  if (!list) return;
  const pager = root.querySelector('[data-cross-sell-pager]');
  if (!pager) return;
  const prev = root.querySelector('[data-cross-sell-prev]');
  const next = root.querySelector('[data-cross-sell-next]');

  const sl = list.scrollLeft;
  const cw = list.clientWidth;
  const sw = list.scrollWidth;
  const hasOverflow = sw - cw > SCROLL_TOLERANCE_PX;
  const atStart = sl <= SCROLL_TOLERANCE_PX;
  const atEnd = sl + cw >= sw - SCROLL_TOLERANCE_PX;

  if (prev) prev.setAttribute('aria-disabled', !hasOverflow || atStart ? 'true' : 'false');
  if (next) next.setAttribute('aria-disabled', !hasOverflow || atEnd ? 'true' : 'false');
}

function attachScrollListeners(root) {
  if (root.dataset.the90sCrossSellReady === '1') return;
  const list = root.querySelector('[data-cross-sell-list]');
  if (!list) return;
  root.dataset.the90sCrossSellReady = '1';
  list.addEventListener('scroll', () => updateArrows(root), { passive: true });
  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => updateArrows(root));
    ro.observe(list);
  }
}

function refreshRoot(root) {
  const { state, gap, items } = computeState(root);
  root.dataset.state = state;

  const sorted = sortItems(items, state, gap);
  const list = root.querySelector('[data-cross-sell-list]');
  if (list) {
    // Preservar el orden en el DOM (append reubica sin destruir).
    for (const el of sorted) list.appendChild(el);
  }

  applyCopyForState(root, state, gap);
  attachScrollListeners(root);
  updateArrows(root);
}

function refreshAll() {
  document.querySelectorAll(ROOT_SELECTOR).forEach(refreshRoot);
}

// ---- Delegated pager clicks ------------------------------------------

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const prevBtn = event.target.closest('[data-cross-sell-prev]');
  const nextBtn = event.target.closest('[data-cross-sell-next]');
  const btn = prevBtn || nextBtn;
  if (!btn) return;
  if (btn.getAttribute('aria-disabled') === 'true') return;
  const root = btn.closest(ROOT_SELECTOR);
  if (!root) return;
  const list = root.querySelector('[data-cross-sell-list]');
  if (!list) return;

  // Target absoluto clampeado al rango [0, scrollWidth - clientWidth]: así
  // el último paso siempre llega al final exacto (no deja asomando media
  // card por short-landing). El scroll listener dispara updateArrows cuando
  // el smooth-scroll termina.
  const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth);
  const delta = list.clientWidth * PAGE_FRACTION;
  const target = prevBtn
    ? Math.max(0, list.scrollLeft - delta)
    : Math.min(maxScroll, list.scrollLeft + delta);
  list.scrollTo({ left: target, behavior: 'smooth' });
});

window.addEventListener('resize', () => {
  // Al cambiar el viewport, clientWidth/scrollWidth cambian — recomputamos
  // el estado de las flechas (puede pasar de hasOverflow true a false, etc.).
  document.querySelectorAll(ROOT_SELECTOR).forEach(updateArrows);
}, { passive: true });

// ---- Init + morph refresh --------------------------------------------

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', refreshAll);
} else {
  refreshAll();
}

// El add-to-cart desde el quick-view dispara cartLinesUpdate, que a su vez
// hace morphSection del drawer entero. Consecuencia: el <dialog> del quick-view
// y el botón "Ver" que lo abrió viven dentro del morph — se reemplazan.
//
// Secuencia intencionada:
//   1. Cerrar el dialog sync antes del morph — mientras el trigger aún existe,
//      para no dejar el dialog abierto durante el reemplazo del DOM.
//   2. Después del morph (event.promise.finally), recomputar estado/orden/copy/
//      paginación y recolocar el focus en un anchor estable: por defecto el
//      heading del drawer (#cart-drawer-heading, preservado por morphdom vía
//      id-key). Si el <dialog>.close() nativo intentó restaurar focus al trigger
//      que ya no existe, el browser cae en <body> y este focus manual lo rescata.

// Lista priorizada de anchors estables donde recolocar el focus tras cerrar
// el quick-view + morph. En cart drawer usa #cart-drawer-heading. En PDP
// (cuando el drawer no se abre tras add) cae al heading del cross-sell PDP.
// Fallback final: no hace nada — mejor dejar el browser en su comportamiento
// nativo que forzar un anchor arbitrario.
const STABLE_ANCHORS = [
  '#cart-drawer-heading',
  '#the90s-cross-sell-pdp-heading',
];

function focusStableAnchor() {
  if (document.activeElement && document.activeElement !== document.body) return;
  for (const selector of STABLE_ANCHORS) {
    const anchor = document.querySelector(selector);
    if (anchor instanceof HTMLElement) {
      if (!anchor.hasAttribute('tabindex')) anchor.setAttribute('tabindex', '-1');
      anchor.focus({ preventScroll: true });
      return;
    }
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  document.querySelectorAll(`${DIALOG_SELECTOR}[open]`).forEach((el) => {
    if (el instanceof HTMLDialogElement) el.close();
  });

  const runAfterMorph = () => requestAnimationFrame(() => {
    refreshAll();
    focusStableAnchor();
  });
  if (event.promise && typeof event.promise.finally === 'function') {
    event.promise.finally(runAfterMorph);
  } else {
    runAfterMorph();
  }
});
